import React from 'react';
import {
    FaChalkboardTeacher, FaCheckCircle, FaExclamationCircle, FaHourglassHalf, FaRegCalendarAlt, FaRegClock,
} from 'react-icons/fa';
import styles from './styles/CalendarEvent.module.css';
import { CALENDAR_ROLES, EVENT_STATUS, STATUS_LABELS, describeEvent } from './calendarUtils';

export const STATUS_ICONS = Object.freeze({
    [EVENT_STATUS.UPCOMING]: FaRegClock,
    [EVENT_STATUS.IN_PROGRESS]: FaHourglassHalf,
    [EVENT_STATUS.COMPLETED]: FaCheckCircle,
    [EVENT_STATUS.LATE]: FaExclamationCircle,
    [EVENT_STATUS.NO_DUE_DATE]: FaRegCalendarAlt,
    [EVENT_STATUS.TEACHER]: FaChalkboardTeacher,
});

/** Statuses worth spelling out next to the title; the rest are clear from the icon. */
const LABELLED_STATUSES = new Set([EVENT_STATUS.LATE, EVENT_STATUS.IN_PROGRESS, EVENT_STATUS.COMPLETED]);

/**
 * One event as FullCalendar's `eventContent` renders it.
 *
 * Three independent channels, so none relies on colour alone: the classroom is the colour,
 * the role is filled (student) versus outlined with a chalkboard (teacher), and the status
 * is an icon plus — where it matters — a word.
 *
 * @param {{event: {title: string, extendedProps: Object}, compact?: boolean}} props
 *   `compact` for the month/week grid, where there's only room for the icon and title.
 */
const CalendarEventContent = ({ event, compact = false }) => {
    const { role, status, classroomColor, classroomName } = event.extendedProps;
    const isTeacher = role === CALENDAR_ROLES.TEACHER;
    const Icon = isTeacher ? FaChalkboardTeacher : STATUS_ICONS[status];
    const description = describeEvent(event);

    return (
        <div
            className={[
                styles.event,
                isTeacher ? styles.teacher : styles.student,
                styles[status],
                compact ? styles.compact : '',
            ].join(' ')}
            style={{ '--classroom-color': classroomColor }}
            title={description}
            aria-label={description}
        >
            <span className={styles.swatch} aria-hidden="true" />
            <Icon className={styles.icon} aria-hidden="true" />
            <span className={styles.title}>{event.title}</span>
            {!compact && <span className={styles.classroom}>{classroomName}</span>}
            {!compact && LABELLED_STATUSES.has(status) && (
                <span className={styles.statusLabel}>{STATUS_LABELS[status]}</span>
            )}
        </div>
    );
};

export default CalendarEventContent;
